// Client entity type (the KYC client / account holder): operating company, holding (active or passive),
// domiciliary company (PIC), trust (named as the trust or the trustee), foundation or quoted company.
//
// The ownership step stores it in several fields read by desktop, mobile, staff and the save sanitizer
// (desktopClientEntityType / desktopClientHoldingKind, uboFlags, preliminaryNoneOfAbove,
// mobileAssistantDraft.preliminaryMode, accountHolderTrustResolution / accountHolderTrustNameKind).
// applyClientEntityType writes the complete set, so a choice made outside the ownership step (the
// company upload-first step of kyc-sent) reads the same on desktop, mobile and the staff side.

import iconProduction from "../images/icon-production.svg";
import iconHolding from "../images/icon-holding.svg";
import iconPic from "../images/icon-pic.svg";
import iconTrust from "../images/icon-trust2.svg";
import iconFoundation from "../images/icon-foundation.svg";
import iconQuoted from "../images/icon-quoted.svg";

export const CLIENT_ENTITY_TYPES = [
  { value: "operating", title: "Operating company", hint: "A standard non-quoted company carrying out commercial business activities.", iconSrc: iconProduction },
  { value: "holding", title: "Holding company", hint: "A company primarily used to hold interests in other companies.", iconSrc: iconHolding },
  { value: "pic", title: "Domiciliary company (PIC)", hint: "A personal investment company used to hold assets or investments.", iconSrc: iconPic },
  { value: "trust", title: "Trust", hint: "A legal arrangement where trustees hold or manage assets for beneficiaries.", iconSrc: iconTrust },
  { value: "foundation", title: "Foundation", hint: "A legal arrangement with separate legal personality for beneficiaries or a purpose.", iconSrc: iconFoundation },
  { value: "quoted", title: "Quoted company", hint: "A company listed on a regulated stock exchange.", iconSrc: iconQuoted },
];
export const HOLDING_KINDS = [
  { value: "active", label: "Active holding", hint: "Holds a majority stake in one or more operating companies; does not mainly administer third-party assets." },
  { value: "passive", label: "Passive holding", hint: "Mainly combines and manages the assets of a family or a defined group." },
];
export const TRUST_KINDS = [
  { value: "trust", label: "The client name is the trust's name", hint: "The client name refers to the trust itself." },
  { value: "trustee", label: "The client name is the trustee's name", hint: "The client name refers to the trustee acting for the trust." },
];

// Upload-first company documents are requested for these types only.
export const UPLOAD_FIRST_ENTITY_TYPES = ["operating", "holding", "pic"];

const MOBILE_MODES = { operating: "noneOfAbove", pic: "pic", trust: "trust", foundation: "foundation", quoted: "quoted" };
const fromMobileMode = {
  noneOfAbove: { type: "operating" }, holdingActive: { type: "holding", holdingKind: "active" },
  holdingPassive: { type: "holding", holdingKind: "passive" }, holding: { type: "holding" },
  pic: { type: "pic" }, trust: { type: "trust" }, foundation: { type: "foundation" }, quoted: { type: "quoted" },
};
const clean = (value) => String(value || "").trim();

// Saved choice, read in the same order as the desktop ownership step.
export function clientEntityTypeOf(ownership = {}) {
  const flags = ownership?.uboFlags || {};
  const mobile = fromMobileMode[clean(ownership?.mobileAssistantDraft?.preliminaryMode)] || {};
  let type = clean(ownership?.desktopClientEntityType);
  if (!type) {
    type = flags.quotedCompany ? "quoted" : flags.accountHolderTrust ? "trust" : flags.accountHolderFoundation ? "foundation"
      : mobile.type || (flags.pctNotMeaningful ? "pic" : "");
  }
  const holdingKind = type === "holding" ? clean(ownership?.desktopClientHoldingKind) || mobile.holdingKind || "" : "";
  const trustKind = type === "trust" ? clean(ownership?.accountHolderTrustResolution?.kind) || clean(ownership?.accountHolderTrustNameKind) : "";
  return { type, holdingKind, trustKind };
}

export const entityTypeComplete = ({ type, holdingKind, trustKind } = {}) =>
  !!type && (type !== "holding" || !!holdingKind) && (type !== "trust" || !!trustKind);

export function entityTypeLabel({ type, holdingKind } = {}) {
  const base = CLIENT_ENTITY_TYPES.find((t) => t.value === type)?.title || "";
  if (type === "holding" && holdingKind) return `${base} · ${holdingKind === "active" ? "active" : "passive"}`;
  return base;
}

// True when applying this choice would clear ownership details already entered.
export function typeChangeClearsDetails(ownership = {}, choice = {}) {
  const previous = clientEntityTypeOf(ownership);
  return !!previous.type && previous.type !== choice.type && hasOwnershipDetails(ownership);
}

export function hasOwnershipDetails(ownership = {}) {
  const named = (list) => (Array.isArray(list) ? list : []).some((row) => clean(row?.name));
  return named(ownership?.shareholders) || named(ownership?.directShareholders) || named(ownership?.ubos) ||
    named(ownership?.legalArrangements?.arrangements) ||
    (Array.isArray(ownership?.assistantTree?.nodes) && ownership.assistantTree.nodes.length > 1) ||
    (Array.isArray(ownership?.mobileAssistantDraft?.roots) && ownership.mobileAssistantDraft.roots.length > 0);
}

// Data that depends on the type (same as the desktop ownership step clears on a type change).
const TYPE_DEPENDENT_RESET = {
  shareholders: [], directShareholders: [], ubos: [], controllingPersonsNatural: [],
  legalArrangements: { arrangements: [] }, controllingPersonsLegalArrangements: { arrangements: [] },
  assistantTree: null, assistantRoots: [], desktopAssistantMode: "", assistantGenerated: false,
  uboAssessment: null, qualifiedUbos: [], mobileEntryMode: "",
  desktopClientStructureType: "", desktopClientCompletionMethod: "",
};

/**
 * Ownership with the client entity type set. Changing from one saved type to another clears the ownership
 * details that depend on it (ask the client first: typeChangeClearsDetails); a first answer, or the same type
 * with a new holding / trust answer, keeps them.
 */
export function applyClientEntityType(ownership = {}, { type, holdingKind = "", trustKind = "" }, customerName = "") {
  const previous = clientEntityTypeOf(ownership);
  // A first answer (no type saved yet) keeps whatever was already entered.
  const typeChanged = !!previous.type && previous.type !== type;
  const nextHolding = type === "holding" ? holdingKind : "";
  const nextTrust = type === "trust" ? trustKind : "";
  const mobileMode = type === "holding" ? (nextHolding === "active" ? "holdingActive" : nextHolding === "passive" ? "holdingPassive" : "holding")
    : MOBILE_MODES[type] || "";
  const base = typeChanged ? { ...ownership, ...TYPE_DEPENDENT_RESET } : { ...ownership };
  return {
    ...base,
    desktopClientEntityType: type || "",
    desktopClientHoldingKind: nextHolding,
    preliminaryNoneOfAbove: type === "operating" || (type === "holding" && nextHolding === "active"),
    uboFlags: {
      ...(ownership?.uboFlags || {}),
      pctNotMeaningful: type === "pic" || (type === "holding" && nextHolding === "passive"),
      quotedCompany: type === "quoted",
      accountHolderTrust: type === "trust",
      accountHolderFoundation: type === "foundation",
    },
    accountHolderTrustResolution: type === "trust" && nextTrust ? { kind: nextTrust, sourceName: clean(customerName) } : null,
    accountHolderTrustNameKind: nextTrust,
    mobileAssistantDraft: typeChanged || !ownership?.mobileAssistantDraft
      ? { preliminaryMode: mobileMode, directCount: "", roots: [] }
      : { ...ownership.mobileAssistantDraft, preliminaryMode: mobileMode },
  };
}
